import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import config from '../config'
import { useAuth } from '../auth/AuthContext'

export default function MapList({ maps, onMapSelect, onRefresh }) {
  const { user } = useAuth()
  const role = user?.role
  const isSuperAdmin = role === 'SUPER_ADMIN'
  const [modal, setModal] = useState(null)
  const [toasts, setToasts] = useState([])
  const [backgroundUploading, setBackgroundUploading] = useState(null)
  const [backgroundModal, setBackgroundModal] = useState(null)
  const [overlayUploading, setOverlayUploading] = useState(null)
  const [overlayModal, setOverlayModal] = useState(null)
  const [compassUploading, setCompassUploading] = useState(null)
  const [compassModal, setCompassModal] = useState(null)
  const [statusColorModal, setStatusColorModal] = useState(null) // { map }
  const defaultStatusColors = { AVAILABLE: '#4CAF50', SOLD: '#F44336', LOCKED: '#FFD700' }
  const [statusColorInputs, setStatusColorInputs] = useState(defaultStatusColors)
  const [statusColorSaving, setStatusColorSaving] = useState(false)
  const [stageTemplateModal, setStageTemplateModal] = useState(null) // { map }
  const [stageTemplateInput, setStageTemplateInput] = useState('default')
  const [stageTemplateSaving, setStageTemplateSaving] = useState(false)

  // Update modal map data when maps list refreshes
  useEffect(() => {
    if (stageTemplateModal?.map) {
      const updatedMap = maps.find(m => m.id === stageTemplateModal.map.id)
      if (updatedMap && updatedMap.stageTemplate !== stageTemplateModal.map.stageTemplate) {
        setStageTemplateModal({ map: updatedMap })
        setStageTemplateInput(updatedMap.stageTemplate || 'default')
      }
    }
  }, [maps, stageTemplateModal])

  const pushToast = (message, variant = 'success') => {
    const id = Math.random().toString(36).slice(2)
    setToasts((t) => [...t, { id, message, variant }])
    setTimeout(() => {
      setToasts((t) => t.filter(x => x.id !== id))
    }, 2500)
  }
  const confirmDelete = (map) => setModal({ type: 'delete', map })

  const handleDelete = async (mapId) => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(config.getApiUrl(`/maps/${mapId}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (response.ok) {
        setModal(null)
        onRefresh()
        pushToast('Map deleted', 'success')
      } else {
        pushToast('Failed to delete map', 'error')
      }
    } catch (err) {
      console.error('Delete failed:', err)
      pushToast('Failed to delete map', 'error')
    }
  }

  const handleShare = async (map) => {
    const base = window.location.origin
    const url = `${base}/viewer?mapId=${map.id}`
    // Try modern Clipboard API first
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url)
        pushToast('link copied')
        return
      }
      throw new Error('Clipboard API unavailable')
    } catch (e1) {
      // Fallback: use a temporary input + execCommand
      try {
        const temp = document.createElement('input')
        temp.value = url
        document.body.appendChild(temp)
        temp.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(temp)
        if (ok) {
          pushToast('link copied')
          return
        }
        throw new Error('execCommand copy failed')
      } catch (e2) {
        // Final fallback: show prompt so user can manually copy
        try {
          window.prompt('Copy share link:', url)
          pushToast('Share link shown')
        } catch (e3) {
          console.error('Copy failed', e1, e2, e3)
          pushToast('Failed to copy link', 'error')
        }
      }
    }
  }

  const handlePreview = (map) => {
    const base = window.location.origin
    const url = `${base}/viewer?mapId=${map.id}&preview=true`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const openBackgroundModal = (map) => {
    setBackgroundModal({ map })
  }

  const closeBackgroundModal = () => {
    setBackgroundModal(null)
  }

  const openOverlayModal = (map) => {
    setOverlayModal({ map })
  }

  const closeOverlayModal = () => {
    setOverlayModal(null)
  }

  const openCompassModal = (map) => {
    setCompassModal({ map })
  }

  const closeCompassModal = () => {
    setCompassModal(null)
  }

  const handleBackgroundChange = async (map, event) => {
    const file = event.target.files[0]
    if (!file) return

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      pushToast('Please select a valid image file (JPEG, PNG, GIF, or WebP)', 'error')
      return
    }

    // Validate file size (15MB limit)
    if (file.size > 15 * 1024 * 1024) {
      pushToast('File size must be less than 15MB', 'error')
      return
    }

    setBackgroundUploading(map.id)
    try {
      const formData = new FormData()
      formData.append('background', file)

      const token = localStorage.getItem('token')
      const response = await fetch(config.getApiUrl(`/maps/${map.id}/background`), {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      })

      if (response.ok) {
        pushToast('Background updated successfully')
        onRefresh()
        closeBackgroundModal()
      } else {
        const errorData = await response.json()
        pushToast(`Failed to update background: ${errorData.error}`, 'error')
      }
    } catch (err) {
      console.error('Background update failed:', err)
      pushToast('Failed to update background', 'error')
    } finally {
      setBackgroundUploading(null)
      // Reset file input
      event.target.value = ''
    }
  }

  const handleOverlayChange = async (map, event) => {
    const file = event.target.files[0]
    if (!file) return

    const lower = file.name.toLowerCase()
    const isSvg = file.type === 'image/svg+xml' || lower.endsWith('.svg')
    const isPng = file.type === 'image/png' || lower.endsWith('.png')
    const isJpg = file.type === 'image/jpeg' || lower.endsWith('.jpg') || lower.endsWith('.jpeg')
    if (!isSvg && !isPng && !isJpg) {
      pushToast('Please select a valid SVG, PNG, or JPG file', 'error')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      pushToast('File size must be less than 5MB', 'error')
      return
    }

    setOverlayUploading(map.id)
    try {
      const formData = new FormData()
      formData.append('backgroundSvg', file)

      const token = localStorage.getItem('token')
      const response = await fetch(config.getApiUrl(`/maps/${map.id}/background-svg`), {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      })

      if (response.ok) {
        pushToast('Value overlay updated successfully')
        onRefresh()
        closeOverlayModal()
      } else {
        const errorData = await response.json()
        pushToast(`Failed to update value overlay: ${errorData.error}`, 'error')
      }
    } catch (err) {
      console.error('Overlay update failed:', err)
      pushToast('Failed to update value overlay', 'error')
    } finally {
      setOverlayUploading(null)
      event.target.value = ''
    }
  }

  const handleCompassChange = async (map, event) => {
    const file = event.target.files[0]
    if (!file) return

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']
    if (!allowedTypes.includes(file.type)) {
      pushToast('Please select a valid image file (JPEG, PNG, GIF, WebP, or SVG)', 'error')
      return
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      pushToast('File size must be less than 5MB', 'error')
      return
    }

    setCompassUploading(map.id)
    try {
      const formData = new FormData()
      formData.append('compass', file)

      const token = localStorage.getItem('token')
      const response = await fetch(config.getApiUrl(`/maps/${map.id}/compass`), {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      })

      if (response.ok) {
        pushToast('Compass updated successfully')
        onRefresh()
        closeCompassModal()
      } else {
        const errorData = await response.json()
        pushToast(`Failed to update compass: ${errorData.error}`, 'error')
      }
    } catch (err) {
      console.error('Compass update failed:', err)
      pushToast('Failed to update compass', 'error')
    } finally {
      setCompassUploading(null)
      // Reset file input
      event.target.value = ''
    }
  }

  const getStatusColor = (map, status) => {
    if (map?.statusColors && map.statusColors[status]) {
      return map.statusColors[status]
    }
    switch (status) {
      case 'AVAILABLE': return '#4CAF50'
      case 'SOLD': return '#F44336'
      case 'LOCKED': return '#FFD700'
      default: return '#666'
    }
  }

  const openStatusColorModal = (map) => {
    const colors = map?.statusColors && typeof map.statusColors === 'object'
      ? { ...defaultStatusColors, ...map.statusColors }
      : { ...defaultStatusColors }
    setStatusColorInputs(colors)
    setStatusColorModal({ map })
  }

  const closeStatusColorModal = () => {
    setStatusColorSaving(false)
    setStatusColorModal(null)
  }

  const handleStatusColorInputChange = (status, value) => {
    if (!/^#[0-9A-Fa-f]{0,6}$/.test(value)) return
    setStatusColorInputs(prev => ({ ...prev, [status]: value }))
  }

  const handleStatusColorSave = async () => {
    if (!statusColorModal?.map) return
    const map = statusColorModal.map
    const hasInvalid = Object.values(statusColorInputs).some(color => !/^#[0-9A-Fa-f]{6}$/.test(color))
    if (hasInvalid) {
      pushToast('Please enter valid hex colors (e.g., #FF0000)', 'error')
      return
    }
    setStatusColorSaving(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(config.getApiUrl(`/maps/${map.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: map.name,
          statusColors: JSON.stringify(statusColorInputs)
        })
      })
      if (!response.ok) {
        throw new Error('Failed to update status colors')
      }
      try {
        await fetch(config.getApiUrl(`/regions/${map.id}/publish`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        })
      } catch (publishErr) {
        console.warn('Publish after status color update failed', publishErr)
      }
      closeStatusColorModal()
      pushToast('Status colors updated')
      onRefresh()
    } catch (error) {
      console.error('Status color update failed', error)
      pushToast('Failed to update status colors', 'error')
    } finally {
      setStatusColorSaving(false)
    }
  }

  const openStageTemplateModal = (map) => {
    // Find the latest map data from the maps list to ensure we have the most recent stageTemplate
    const latestMap = maps.find(m => m.id === map.id) || map
    const template = latestMap?.stageTemplate || 'default'
    setStageTemplateInput(template)
    setStageTemplateModal({ map: latestMap })
  }

  const closeStageTemplateModal = () => {
    setStageTemplateSaving(false)
    setStageTemplateModal(null)
  }

  const handleStageTemplateSave = async () => {
    if (!stageTemplateModal?.map) return
    const map = stageTemplateModal.map
    if (stageTemplateInput !== 'default' && stageTemplateInput !== 'modern') {
      pushToast('Please select a valid template option', 'error')
      return
    }
    setStageTemplateSaving(true)
    try {
      const token = localStorage.getItem('token')
      const requestBody = {
        name: map.name,
        stageTemplate: stageTemplateInput
      }
      console.log('[MapList] Saving stageTemplate:', requestBody)
      const response = await fetch(config.getApiUrl(`/maps/${map.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to update stage template')
      }
      try {
        await fetch(config.getApiUrl(`/regions/${map.id}/publish`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        })
      } catch (publishErr) {
        console.warn('Publish after stage template update failed', publishErr)
      }
      closeStageTemplateModal()
      pushToast('Stage template updated')
      // Wait a bit for the database to update, then refresh
      setTimeout(() => {
        onRefresh()
      }, 300)
    } catch (error) {
      console.error('Stage template update failed', error)
      pushToast(`Failed to update stage template: ${error.message}`, 'error')
    } finally {
      setStageTemplateSaving(false)
    }
  }

  if (maps.length === 0) {
    return (
      <div className="glass glass-card" style={{ textAlign: 'center' }}>
        <div className="title" style={{ color: 'var(--muted)' }}>No maps yet</div>
        <div className="muted">Upload your first SVG map to get started</div>
      </div>
    )
  }

  return (
    <div className="table-wrap">
      {/* Toasts */}
      {toasts.length > 0 && (
        <div className="toast-container">
          {toasts.map(t => (
            <div key={t.id} className={`toast ${t.variant === 'error' ? 'toast-error' : 'toast-success'}`}>{t.message}</div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modal?.type === 'delete' && createPortal(
        (
          <div className="modal-overlay" role="dialog" aria-modal="true">
            <div className="modal">
              <div className="modal-header">
                <div className="modal-title">Delete Map</div>
                <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={() => setModal(null)}>✕</button>
              </div>
              <div className="modal-body">
                Are you sure you want to delete "{modal.map?.name}"? This action cannot be undone.
              </div>
              <div className="modal-actions">
                <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
                <button className="btn btn-danger" onClick={() => handleDelete(modal.map.id)}>Delete</button>
              </div>
            </div>
          </div>
        ),
        document.body
      )}

      {/* Background Change Modal */}
      {backgroundModal && createPortal(
        (
          <div className="modal-overlay" role="dialog" aria-modal="true">
            <div className="modal background-modal">
              <div className="modal-header">
                <div className="modal-title">Change Background - {backgroundModal.map?.name}</div>
                <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={closeBackgroundModal}>✕</button>
              </div>
              <div className="modal-body">
                <div className="background-preview-container">
                  <div className="current-background">
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: '600', color: 'var(--text)' }}>Current Background</h4>
                    {backgroundModal.map?.backgroundUrl ? (
                      <div className="background-preview">
                        <img 
                          src={config.getApiUrl(backgroundModal.map.backgroundUrl)} 
                          alt="Current background"
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '100%', 
                            objectFit: 'contain'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none'
                            e.target.nextSibling.style.display = 'flex'
                          }}
                        />
                        <div style={{ 
                          display: 'none', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          height: '100%', 
                          background: '#f9fafb', 
                          color: '#6b7280',
                          fontSize: '12px'
                        }}>
                          No background image
                        </div>
                      </div>
                    ) : (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '100%', 
                        background: '#f9fafb', 
                        color: '#6b7280',
                        fontSize: '12px'
                      }}>
                        No background image
                      </div>
                    )}
                  </div>
                  
                  <div className="new-background">
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: '600', color: 'var(--text)' }}>Choose New Background</h4>
                    <div className="file-upload-area">
                      <input
                        type="file"
                        id="background-upload"
                        accept="image/*"
                        onChange={(e) => handleBackgroundChange(backgroundModal.map, e)}
                        style={{ display: 'none' }}
                        disabled={backgroundUploading === backgroundModal.map.id}
                      />
                      <label 
                        htmlFor="background-upload" 
                        className="file-upload-label"
                        style={{ 
                          cursor: backgroundUploading === backgroundModal.map.id ? 'not-allowed' : 'pointer',
                          opacity: backgroundUploading === backgroundModal.map.id ? 0.6 : 1
                        }}
                      >
                        <div className="file-upload-content">
                          {backgroundUploading === backgroundModal.map.id ? (
                            <>
                              <div style={{ width: 20, height: 20, border: '2px solid #3B82F6', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '6px' }}></div>
                              <div style={{ fontSize: '12px' }}>Uploading...</div>
                            </>
                          ) : (
                            <>
                              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '6px' }}>
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                <polyline points="14,2 14,8 20,8"/>
                                <line x1="16" y1="13" x2="8" y2="13"/>
                                <line x1="16" y1="17" x2="8" y2="17"/>
                                <polyline points="10,9 9,9 8,9"/>
                              </svg>
                              <div style={{ fontSize: '12px' }}>Click to select new image</div>
                              <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                                JPEG, PNG, GIF, WebP (max 15MB)
                              </div>
                            </>
                          )}
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn btn-ghost" onClick={closeBackgroundModal}>Cancel</button>
              </div>
            </div>
          </div>
        ),
        document.body
      )}

      {/* Overlay Change Modal */}
      {overlayModal && createPortal(
        (
          <div className="modal-overlay" role="dialog" aria-modal="true">
            <div className="modal background-modal">
              <div className="modal-header">
                <div className="modal-title">Change Value Overlay - {overlayModal.map?.name}</div>
                <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={closeOverlayModal}>✕</button>
              </div>
              <div className="modal-body">
                <div className="background-preview-container">
                  <div className="current-background">
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: '600', color: 'var(--text)' }}>Current Overlay</h4>
                    {overlayModal.map?.backgroundSvgUrl ? (
                      <div className="background-preview">
                        <img 
                          src={config.getApiUrl(overlayModal.map.backgroundSvgUrl)} 
                          alt="Current overlay"
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '100%', 
                            objectFit: 'contain'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none'
                            e.target.nextSibling.style.display = 'flex'
                          }}
                        />
                        <div style={{ 
                          display: 'none', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          height: '100%', 
                          background: '#f9fafb', 
                          color: '#6b7280',
                          fontSize: '12px'
                        }}>
                          Overlay preview unavailable
                        </div>
                      </div>
                    ) : (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '100%', 
                        background: '#f9fafb', 
                        color: '#6b7280',
                        fontSize: '12px'
                      }}>
                        No overlay uploaded
                      </div>
                    )}
                  </div>

                  <div className="new-background">
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: '600', color: 'var(--text)' }}>Choose New Overlay</h4>
                    <div className="file-upload-area">
                      <input
                        type="file"
                        id="overlay-upload"
                        accept=".svg,.png,.jpg,.jpeg,image/svg+xml,image/png,image/jpeg"
                        onChange={(e) => handleOverlayChange(overlayModal.map, e)}
                        style={{ display: 'none' }}
                        disabled={overlayUploading === overlayModal.map.id}
                      />
                      <label 
                        htmlFor="overlay-upload" 
                        className="file-upload-label"
                        style={{ 
                          cursor: overlayUploading === overlayModal.map.id ? 'not-allowed' : 'pointer',
                          opacity: overlayUploading === overlayModal.map.id ? 0.6 : 1
                        }}
                      >
                        <div className="file-upload-content">
                          {overlayUploading === overlayModal.map.id ? (
                            <>
                              <div style={{ width: 20, height: 20, border: '2px solid #7c3aed', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '6px' }}></div>
                              <div style={{ fontSize: '12px' }}>Uploading...</div>
                            </>
                          ) : (
                            <>
                              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '6px' }}>
                                <rect x="3" y="3" width="18" height="6" rx="1" ry="1" />
                                <rect x="3" y="10" width="18" height="6" rx="1" ry="1" />
                                <rect x="3" y="17" width="18" height="4" rx="1" ry="1" />
                              </svg>
                              <div style={{ fontSize: '12px' }}>Click to select new SVG</div>
                              <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                                SVG, PNG, JPG (max 5MB)
                              </div>
                            </>
                          )}
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn btn-ghost" onClick={closeOverlayModal}>Cancel</button>
              </div>
            </div>
          </div>
        ),
        document.body
      )}

      {/* Compass Change Modal */}
      {compassModal && createPortal(
        (
          <div className="modal-overlay" role="dialog" aria-modal="true">
            <div className="modal background-modal">
              <div className="modal-header">
                <div className="modal-title">Change Compass - {compassModal.map?.name}</div>
                <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={closeCompassModal}>✕</button>
              </div>
              <div className="modal-body">
                <div className="background-preview-container">
                  <div className="current-background">
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: '600', color: 'var(--text)' }}>Current Compass</h4>
                    {compassModal.map?.compassUrl ? (
                      <div className="background-preview">
                        <img 
                          src={config.getApiUrl(compassModal.map.compassUrl)} 
                          alt="Current compass"
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '100%', 
                            objectFit: 'contain'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none'
                            e.target.nextSibling.style.display = 'flex'
                          }}
                        />
                        <div style={{ 
                          display: 'none', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          height: '100%', 
                          background: '#f9fafb', 
                          color: '#6b7280',
                          fontSize: '12px'
                        }}>
                          No compass image
                        </div>
                      </div>
                    ) : (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '100%', 
                        background: '#f9fafb', 
                        color: '#6b7280',
                        fontSize: '12px'
                      }}>
                        No compass image
                      </div>
                    )}
                  </div>
                  
                  <div className="new-background">
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: '600', color: 'var(--text)' }}>Choose New Compass</h4>
                    <div className="file-upload-area">
                      <input
                        type="file"
                        id="compass-upload"
                        accept="image/*,image/svg+xml"
                        onChange={(e) => handleCompassChange(compassModal.map, e)}
                        style={{ display: 'none' }}
                        disabled={compassUploading === compassModal.map.id}
                      />
                      <label 
                        htmlFor="compass-upload" 
                        className="file-upload-label"
                        style={{ 
                          cursor: compassUploading === compassModal.map.id ? 'not-allowed' : 'pointer',
                          opacity: compassUploading === compassModal.map.id ? 0.6 : 1
                        }}
                      >
                        <div className="file-upload-content">
                          {compassUploading === compassModal.map.id ? (
                            <>
                              <div style={{ width: 20, height: 20, border: '2px solid #3B82F6', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '6px' }}></div>
                              <div style={{ fontSize: '12px' }}>Uploading...</div>
                            </>
                          ) : (
                            <>
                              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '6px' }}>
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                <polyline points="14,2 14,8 20,8"/>
                              </svg>
                              <div style={{ fontSize: '12px' }}>Click to select new image</div>
                              <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                                JPEG, PNG, GIF, WebP, SVG (max 5MB)
                              </div>
                            </>
                          )}
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn btn-ghost" onClick={closeCompassModal}>Cancel</button>
              </div>
            </div>
          </div>
        ),
        document.body
      )}

      {/* Status Color Modal */}
      {statusColorModal && createPortal(
        (
          <div className="modal-overlay" role="dialog" aria-modal="true">
            <div className="modal" style={{ maxWidth: 520 }}>
              <div className="modal-header">
                <div className="modal-title">Status Colors — {statusColorModal.map?.name}</div>
                <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={closeStatusColorModal}>✕</button>
              </div>
              <div className="modal-body">
                <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                  Customize the colors used for Available, Sold, and Locked lots for this plan.
                </p>
                <div style={{ display: 'grid', gap: 16 }}>
                  {(['AVAILABLE', 'SOLD', 'LOCKED']).map(status => (
                    <div key={status} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, background: '#f9fafb' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                        {status === 'AVAILABLE' ? 'Available' : status === 'SOLD' ? 'Sold' : 'Locked'}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input
                          type="color"
                          value={statusColorInputs[status]}
                          onChange={(e) => handleStatusColorInputChange(status, e.target.value)}
                          style={{
                            width: 48,
                            height: 32,
                            border: '1px solid #d1d5db',
                            borderRadius: 6,
                            cursor: 'pointer'
                          }}
                        />
                        <input
                          type="text"
                          value={statusColorInputs[status]}
                          onChange={(e) => handleStatusColorInputChange(status, e.target.value)}
                          placeholder="#FFFFFF"
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: 6,
                            fontFamily: 'monospace'
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn btn-ghost" onClick={closeStatusColorModal} disabled={statusColorSaving}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleStatusColorSave} disabled={statusColorSaving}>
                  {statusColorSaving ? 'Saving...' : 'Save Colors'}
                </button>
              </div>
            </div>
          </div>
        ),
        document.body
      )}

      {/* Stage Template Modal */}
      {stageTemplateModal && createPortal(
        (
          <div className="modal-overlay" role="dialog" aria-modal="true">
            <div className="modal" style={{ maxWidth: 520 }}>
              <div className="modal-header">
                <div className="modal-title">Stage Template — {stageTemplateModal.map?.name}</div>
                <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={closeStageTemplateModal}>✕</button>
              </div>
              <div className="modal-body">
                <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                  Choose the template style for the stages UI in the viewer.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: 16,
                      border: `2px solid ${stageTemplateInput === 'default' ? '#8b5cf6' : '#e5e7eb'}`,
                      borderRadius: 8,
                      background: stageTemplateInput === 'default' ? '#f3f4f6' : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onClick={() => setStageTemplateInput('default')}
                  >
                    <input
                      type="radio"
                      name="stageTemplate"
                      value="default"
                      checked={stageTemplateInput === 'default'}
                      onChange={() => setStageTemplateInput('default')}
                      style={{ margin: 0 }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Default</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>Traditional stage display with standard layout</div>
                    </div>
                  </label>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: 16,
                      border: `2px solid ${stageTemplateInput === 'modern' ? '#8b5cf6' : '#e5e7eb'}`,
                      borderRadius: 8,
                      background: stageTemplateInput === 'modern' ? '#f3f4f6' : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onClick={() => setStageTemplateInput('modern')}
                  >
                    <input
                      type="radio"
                      name="stageTemplate"
                      value="modern"
                      checked={stageTemplateInput === 'modern'}
                      onChange={() => setStageTemplateInput('modern')}
                      style={{ margin: 0 }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Modern</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>Contemporary design with enhanced visual appeal</div>
                    </div>
                  </label>
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn btn-ghost" onClick={closeStageTemplateModal} disabled={stageTemplateSaving}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleStageTemplateSave} disabled={stageTemplateSaving}>
                  {stageTemplateSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        ),
        document.body
      )}

      <table className="table">
        <thead>
          <tr>
            <th style={{ width: '7%' }}>Name</th>
            <th style={{ width: '5%' }}>Lots</th>
            <th style={{ width: '5%', textAlign: 'center' }}>Status</th>
            <th style={{ width: '15%', textAlign: 'center'}}>Lot Availability</th>
            <th style={{ width: '14%', textAlign: 'center' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {maps.map(map => {
            const regions = map.regions || []
            const isPublished = Boolean(map.versions && map.versions.some(v => v && v.published))
            const staged = regions.filter(r => r.name && String(r.name).trim() !== '')
            const statusCounts = ['AVAILABLE', 'SOLD', 'LOCKED'].map(status => ({
              status,
              count: staged.filter(r => r.status === status).length
            }))

            return (
              <tr key={map.id}>
                <td data-label="Name">
                  <div className="table-primary">{map.name}</div>
                </td>
                <td data-label="Regions">{regions.filter(r => r.name && String(r.name).trim() !== '').length}</td>
                <td data-label="Published" style={{ textAlign: 'center', width: '18%' }}>
                  {isPublished ? (
                    <span className="badge badge-success"><span className="dot dot-success"></span> Published</span>
                  ) : (
                    <span className="muted" style={{ fontSize: 12 }}>—</span>
                  )}
                </td>
                <td data-label="Status Summary" style={{ width: '32%', textAlign: 'center' }}>
                  <div className="row gap-8 status-summary-container" style={{ flexWrap: 'nowrap', justifyContent: 'space-between', width: '100%' }}>
                    {statusCounts.map(s => (
                      <div key={s.status} className="status-item" style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                        <div className="dot" style={{ background: getStatusColor(map, s.status) }}></div>
                        <span style={{ fontSize: 12 }}>{s.status}: {s.count}</span>
                      </div>
                    ))}
                  </div>
                </td>
                <td data-label="Actions" style={{ textAlign: 'center', width: '14%' }}>
                  <div className="row gap-8 actions-container" style={{ justifyContent: 'center', flexWrap: 'nowrap' }}>
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handlePreview(map) }}
                      title="Preview in viewer"
                      aria-label="Preview"
                      className="btn btn-sm icon-btn preview-btn"
                      style={{ 
                        background: '#10b981',
                        border: '1px solid #10b981',
                        color: 'white',
                        minWidth: '32px',
                        minHeight: '32px',
                        flexShrink: 0
                      }}
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    </button>
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleShare(map) }}
                      title="Copy public viewer link"
                      aria-label="Share"
                      className="btn btn-sm icon-btn btn-share"
                      style={{ 
                        minWidth: '32px',
                        minHeight: '32px',
                        flexShrink: 0
                      }}
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M4 12v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6"/>
                        <path d="M16 6l-4-4-4 4"/>
                        <path d="M12 2v14"/>
                      </svg>
                    </button>
                    {isSuperAdmin && (
                      <>
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onMapSelect(map) }}
                          aria-label="Edit"
                          className="btn btn-sm icon-btn btn-edit"
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M12 20h9"/>
                            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/>
                          </svg>
                        </button>
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); openBackgroundModal(map) }}
                          title="Change background image"
                          aria-label="Change Background"
                          className="btn btn-sm icon-btn"
                          style={{ 
                            background: '#3B82F6',
                            border: '1px solid #3B82F6',
                            color: 'white'
                          }}
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                            <circle cx="8.5" cy="8.5" r="1.5"/>
                            <polyline points="21,15 16,10 5,21"/>
                          </svg>
                        </button>
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); openOverlayModal(map) }}
                          title="Change value overlay"
                          aria-label="Change Value Overlay"
                          className="btn btn-sm icon-btn"
                          style={{
                            background: '#7c3aed',
                            border: '1px solid #7c3aed',
                            color: 'white'
                          }}
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <rect x="3" y="3" width="18" height="6" rx="1" ry="1" />
                            <rect x="3" y="10" width="18" height="6" rx="1" ry="1" />
                            <rect x="3" y="17" width="18" height="4" rx="1" ry="1" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); openCompassModal(map) }}
                          title="Change compass image"
                          aria-label="Change Compass"
                          className="btn btn-sm icon-btn"
                          style={{ 
                            background: '#10B981',
                            border: '1px solid #10B981',
                            color: 'white'
                          }}
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="12"/>
                            <line x1="12" y1="16" x2="12.01" y2="16"/>
                            <polyline points="8 12 12 8 16 12"/>
                          </svg>
                        </button>
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); openStatusColorModal(map) }}
                          title="Edit status colors"
                          aria-label="Edit Status Colors"
                          className="btn btn-sm icon-btn"
                          style={{
                            background: '#f59e0b',
                            border: '1px solid #f59e0b',
                            color: 'white'
                          }}
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <circle cx="12" cy="12" r="10"/>
                            <path d="M7 15l3.5-3.5"/>
                            <path d="M10.5 11.5l2 2 4-4"/>
                          </svg>
                        </button>
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); openStageTemplateModal(map) }}
                          title="Edit stage template"
                          aria-label="Edit Stage Template"
                          className="btn btn-sm icon-btn"
                          style={{
                            background: '#8b5cf6',
                            border: '1px solid #8b5cf6',
                            color: 'white'
                          }}
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                            <line x1="9" y1="3" x2="9" y2="21"/>
                            <line x1="15" y1="3" x2="15" y2="21"/>
                          </svg>
                        </button>
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); confirmDelete(map) }}
                          aria-label="Delete"
                          className="btn btn-sm icon-btn btn-delete"
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                            <path d="M10 11v6"/>
                            <path d="M14 11v6"/>
                            <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
